type QuoteAddOnSection = {
  title?: string;
  items?: Array<{
    label?: string;
    amount?: unknown;
    premium?: unknown;
    value?: unknown;
    is_checked?: boolean;
  }>;
};

type QuoteWithSections = {
  sections?: QuoteAddOnSection[];
};

type QuoteAddOnItem = {
  label?: string;
  amount?: unknown;
  premium?: unknown;
  value?: unknown;
  is_checked?: boolean;
  [key: string]: unknown;
};

type QuotePrice = {
  amount?: unknown;
  amount_with_vat?: unknown;
  vat?: unknown;
};

const ADD_ON_SECTION_TITLES = new Set(["add-ons", "add ons", "addons"]);

const normalizeLabel = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isAddonSectionTitle = (title?: string) => ADD_ON_SECTION_TITLES.has(normalizeLabel(title));

export const getQuotePriceNumber = (value: unknown) => {
  const parsedValue =
    typeof value === "string"
      ? Number(value.replace(/,/g, "").replace(/[^0-9.-]/g, ""))
      : Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const getQuoteAddOnPriceNumber = (item: QuoteAddOnItem) =>
  getQuotePriceNumber(item?.amount ?? item?.premium ?? item?.value);

export function getQuoteAddOnLabels(quote: QuoteWithSections | null | undefined) {
  const addOnItems = getQuoteAddOnItems(quote);

  return addOnItems
    .map((item) => item?.label?.trim())
    .filter((label): label is string => Boolean(label));
}

export function getUniqueQuoteAddOnLabels(quotes: Array<QuoteWithSections | null | undefined>) {
  const seenLabels = new Set<string>();

  quotes.forEach((quote) => {
    getQuoteAddOnLabels(quote).forEach((label) => {
      seenLabels.add(label);
    });
  });

  return Array.from(seenLabels);
}

export function getQuoteAddOnItems(quote: QuoteWithSections | null | undefined) {
  const explicitAddOnSection = quote?.sections?.find((section) =>
    isAddonSectionTitle(section?.title)
  );

  if (explicitAddOnSection?.items?.length) {
    return explicitAddOnSection.items as QuoteAddOnItem[];
  }

  return (quote?.sections || [])
    .flatMap((section) => section?.items || [])
    .filter(
      (item) =>
        Boolean(item?.label) &&
        (item?.is_checked === true || item?.amount !== undefined || item?.premium !== undefined)
    ) as QuoteAddOnItem[];
}

export function getCheckedQuoteAddOnItems(quote: QuoteWithSections | null | undefined) {
  const seenLabels = new Set<string>();

  return getQuoteAddOnItems(quote).filter((item) => {
    if (!item?.is_checked || !item?.label) {
      return false;
    }

    const normalizedItemLabel = normalizeLabel(item.label);
    if (!normalizedItemLabel || seenLabels.has(normalizedItemLabel)) {
      return false;
    }

    seenLabels.add(normalizedItemLabel);
    return true;
  });
}

export function getCheckedQuoteAddOnTotal(quote: QuoteWithSections | null | undefined) {
  return getCheckedQuoteAddOnItems(quote).reduce(
    (sum, item) => sum + getQuoteAddOnPriceNumber(item),
    0
  );
}

export function getQuoteDisplayTotal(quote: {
  plan?: { price?: QuotePrice };
  price?: QuotePrice;
  sections?: QuoteAddOnSection[];
}) {
  const price = quote?.plan?.price || quote?.price || {};
  const amount = getQuotePriceNumber(price.amount);
  const amountWithVat = getQuotePriceNumber(price.amount_with_vat);
  const vat = getQuotePriceNumber(price.vat);
  const addOnTotal = getCheckedQuoteAddOnTotal(quote);

  if (!amountWithVat) {
    return amount + vat + addOnTotal;
  }

  const baseWithVat = amount + vat;
  const totalWithAddOns = baseWithVat + addOnTotal;

  if (addOnTotal > 0 && Math.abs(amountWithVat - baseWithVat) < 0.01) {
    return totalWithAddOns;
  }

  return amountWithVat;
}
